import type { FormGroup } from '@angular/forms';
import { DropdownData, Input, Value } from './input.interface';

export type FormButtonType = 'callback' | 'routerLink' | 'externalLink';


export interface ReactiveDropdownDataSourceContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup | null;
    readonly previousData: T | null;
    readonly changedField: string | null;
}

export type ReactiveDropdownDataSourceParams<T = unknown> =
    Record<string, unknown> |
    ((context: ReactiveDropdownDataSourceContext<T>) => Record<string, unknown>);

export type ReactiveDropdownResponseMapper<T = unknown> = (
    response: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => DropdownData<unknown> | Value<unknown>[] | unknown[];

export type ReactiveDropdownOptionMapper<T = unknown> = (
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => Value<unknown>;

export type ReactiveDropdownOptionFilter<T = unknown> = (
    option: Value<unknown>,
    context: ReactiveDropdownDataSourceContext<T>,
) => boolean;

export interface ReactiveDropdownDataSource<T = unknown> {
    endpoint: unknown;
    cacheKey: string;
    lazy?: boolean;
    dependsOn?: readonly string[];
    params?: ReactiveDropdownDataSourceParams<T>;
    ttlMs?: number;
    timeoutMs?: number;
    forceReload?: boolean;
    responsePath?: string;
    labelKey?: string;
    valueKey?: string;
    mapResponse?: ReactiveDropdownResponseMapper<T>;
    mapOption?: ReactiveDropdownOptionMapper<T>;
    filterOption?: ReactiveDropdownOptionFilter<T>;
}

export interface ReactiveDropdownDependencyBehaviour {
    clearOnParentChange?: boolean;
    keepValueUntilOptionsLoaded?: boolean;
    reloadOnParentChange?: boolean;
    filterLocally?: boolean;
}

export interface FormButton {
    type?: FormButtonType;
    label: string;
    icon?: string;
    class?: string;
    style?: string | Record<string, string>;
    isVisible?: boolean;
    function?: () => void;
    route?: Array<string | number>;
    queryParams?: Record<string, unknown>;
    fragment?: string;
    href?: string;
    target?: string;
    rel?: string;
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    tooltipEvent?: 'hover' | 'focus';
}

export type FormSignatureUploadStrategy = 'taskFile' | 'deferredBase64';

export interface FormSignatureExistingImageConfiguration {
    idField?: string;
    pathPrefix: string;
    extension?: string;
}

export interface FormSignatureConfiguration {
    database?: string;
    folder: string;
    field?: string;
    uploadStrategy?: FormSignatureUploadStrategy;
    existingImageConfiguration?: FormSignatureExistingImageConfiguration;
}


export interface FormUploadButton {
    isEnabled: boolean;
    label: string;
    imageName?: string;
    imageFolder?: string;
    imageDisplay?: 'inline' | 'link' | 'header';
    accept?: string;
    allowedExtensions?: string[];
    invalidFormatMessage?: string;
    uploadUrl?: string;
    viewFolder?: string;
    showPreview?: boolean;
    maxFileSizeBytes?: number;
    idField?: string;
}

export interface Form<T> {
    name: string,
    header: string,
    type: FormType,
    sections: FormSection<T>[],
    table: FormTable | null,
    uploadButton: FormUploadButton,
    uploadButtons?: FormUploadButton[],
    /**
     * Header buttons rendered next to the upload button.
     * `button` is kept for backward compatibility with legacy forms.
     */
    button?: FormButton | FormButton[],
    buttons?: FormButton[],
    signed: boolean,
    signatureConfiguration?: FormSignatureConfiguration,
    payloadTransform?: (payload: Record<string, unknown>) => Record<string, unknown>
}

export interface FormSection<T> {
    name: string,
    fields: FormField<T>[],
    buttons?: FormButton[],
    tables?: FormTable[],
    width: number,
    isVisible: boolean,
    order: number
}

export interface FormField<T> {
    [key: string]: 
        ((it: FormField<T>) => T | string | null | undefined) |
        ((it: FormField<T>) => void) |
        Input<T> |
        FormFieldConditions |
        string[] | 
        string | 
        number | 
        boolean |
        FormButton[] |
        any | 
        undefined;
    name: string;
    vinculatedFields: string[] | undefined;
    label: string;
    input: Input<T>;
    dataSource?: ReactiveDropdownDataSource<T>;
    dependencyBehaviour?: ReactiveDropdownDependencyBehaviour;
    checkForDuplicates?: (it: string) => boolean;
    width: number,
    conditions: FormFieldConditions;
    buttons?: FormButton[],
    order: number;    
    turnVisible?: (it: FormField<T>) => void;
    updateValue?: (it: FormField<T>) => T | string | null | undefined;
}

export interface FormTable {
    title: string,
    showIndex: boolean,
    data: string[] | string[][] | undefined, 
    style: string[] | string[][] | undefined
}

export interface FormFieldConditions {
    isRememberedOnDuplicate: boolean;
    isRequired: boolean;
    isRetained: boolean;
    hasUndoEnabled: boolean;
    isVisible: boolean;
}

export enum FormType {
    CREATION,
    EDITION,
    DUPLICATION,
    DELETION,
    INFORMATIVE,
    EXPORT,
    GENERATION
}
